<template>
  <footer class="gr-footer">
    <div class="gr-container">
      <div class="gr-footer__grid">
        <div class="gr-footer__brand">
          <img
            src="/logo-gotong-royong-indonesia.webp"
            alt="Gotong Royong Indonesia Logo"
            width="168"
            height="50"
            class="gr-footer__logo"
            loading="lazy"
          />
          <p class="gr-footer__tagline">
            Mari Jadi Bagian Dari Kegiatan Positif yang Berdampak Pada
            Masyarakat
          </p>
        </div>

        <nav class="gr-footer__col" aria-labelledby="gr-footer-about">
          <h2 id="gr-footer-about" class="gr-footer__heading">Tentang kami</h2>
          <ul class="gr-footer__list">
            <li v-for="link in navigationLinks" :key="link.title">
              <a
                v-if="link.section"
                :href="`${HOME_ROUTE}#${link.section}`"
                class="gr-footer__link"
                @click.prevent="navigateToSection(HOME_ROUTE, link.section)"
              >
                <v-icon
                  v-if="link.icon"
                  :icon="link.icon"
                  size="16"
                  aria-hidden="true"
                />
                <span>{{ link.title }}</span>
              </a>
              <NuxtLink v-else :to="link.to" class="gr-footer__link">
                <v-icon
                  v-if="link.icon"
                  :icon="link.icon"
                  size="16"
                  aria-hidden="true"
                />
                <span>{{ link.title }}</span>
              </NuxtLink>
            </li>
          </ul>
        </nav>

        <div class="gr-footer__col">
          <h2 id="gr-footer-contact" class="gr-footer__heading">
            Hubungi kami
          </h2>
          <ul class="gr-footer__list" aria-labelledby="gr-footer-contact">
            <li v-for="social in socials" :key="social.name">
              <a
                :href="social.url"
                target="_blank"
                rel="noopener noreferrer"
                class="gr-footer__link"
              >
                <v-icon :icon="social.icon" size="16" aria-hidden="true" />
                <span>{{ social.name }}</span>
              </a>
            </li>
            <li>
              <a href="mailto:kontak@gotongroyong.org" class="gr-footer__link">
                <v-icon icon="mdi-email-outline" size="16" aria-hidden="true" />
                <span>Email</span>
              </a>
            </li>
          </ul>
          <NuxtLink to="/contact" class="gr-btn gr-btn--outline gr-footer__cta">
            <v-icon
              icon="mdi-message-text-outline"
              size="18"
              aria-hidden="true"
            />
            Hubungi Kami
          </NuxtLink>
        </div>
      </div>

      <div class="gr-footer__legal">
        <p class="gr-footer__copy">
          &copy; {{ currentYear }} Gotong Royong Indonesia. Hak Cipta
          Dilindungi.
        </p>
      </div>
    </div>
  </footer>
</template>

<script lang="ts" setup>
import { useAnchorScroll } from "~/common/composables/useAnchorScroll";

const HOME_ROUTE = "/";

const { navigateToSection } = useAnchorScroll();

// Set after mount so the server-rendered document and the client agree.
const currentYear = ref("");
onMounted(() => {
  currentYear.value = new Date().getFullYear().toString();
});

type FooterNavigationLink = {
  title: string;
  section?: string;
  to?: string;
  icon?: string;
};

const navigationLinks: FooterNavigationLink[] = [
  {
    title: "Kelebihan kami",
    section: "kelebihan-kami",
    icon: "mdi-star-outline",
  },
  { title: "Karya Kami", section: "karya-kami", icon: "mdi-palette-outline" },
  // Canonical omits an icon here, which leaves this one row hanging out of the
  // column's icon rail. `mdi-image-multiple-outline` matches the outline set the
  // other four rows use and the gallery the route actually opens.
  {
    title: "Dokumentasi",
    to: "/documentation",
    icon: "mdi-image-multiple-outline",
  },
  {
    title: "Kegiatan",
    to: "/activities",
    icon: "mdi-calendar-check-outline",
  },
  {
    title: "Jejak Aktivitas",
    to: "/activities?status=FINISHED",
    icon: "mdi-history",
  },
];

const socials = [
  {
    name: "Instagram",
    icon: "mdi-instagram",
    url: "https://www.instagram.com/gotongroyong_org/",
  },
  {
    name: "Blog",
    icon: "mdi-rss",
    url: "https://blog.gotongroyong.org/category/kisah-kami/",
  },
];
</script>

<style scoped>
.gr-footer {
  border-top: 1px solid var(--gr-line-strong);
  padding-block: clamp(3rem, 2rem + 3vw, 4.5rem) 0;
  background-color: var(--gr-surface);
}

.gr-footer__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 2.5rem;
}

@media (min-width: 700px) {
  .gr-footer__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 3rem;
  }
}

@media (min-width: 960px) {
  .gr-footer__grid {
    grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr) minmax(0, 1fr);
    gap: clamp(2.5rem, 1rem + 5vw, 5rem);
  }
}

.gr-footer__brand {
  min-width: 0;
}

.gr-footer__logo {
  display: block;
  width: 168px;
  height: 50px;
  margin-bottom: 1.25rem;
  object-fit: contain;
}

.gr-footer__tagline {
  max-width: 34ch;
  margin: 0;
  color: var(--gr-ink-2);
  font-size: 1rem;
  line-height: 1.6;
}

.gr-footer__col {
  min-width: 0;
}

.gr-footer__heading {
  margin: 0 0 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--gr-line);
  color: var(--gr-green);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  line-height: 1;
  text-transform: uppercase;
}

.gr-footer__list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.gr-footer__link {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  color: var(--gr-ink-2);
  font-size: 0.9375rem;
  line-height: 1.4;
  text-decoration: none;
  overflow-wrap: anywhere;
}

.gr-footer__link :deep(.v-icon) {
  flex: 0 0 auto;
  color: var(--gr-green);
}

@media (hover: hover) {
  .gr-footer__link:hover {
    color: var(--gr-green-deep);
    text-decoration: underline;
    text-underline-offset: 3px;
  }
}

.gr-footer__cta {
  margin-top: 1rem;
}

.gr-footer__legal {
  margin-top: clamp(2.5rem, 2rem + 2vw, 3.5rem);
  padding-block: 1.5rem;
  border-top: 1px solid var(--gr-line);
}

.gr-footer__copy {
  margin: 0;
  color: var(--gr-ink-3);
  font-size: 0.8125rem;
  line-height: 1.5;
}
</style>
